import { createSlice, noopReducer, PayloadAction } from 'ngrx-slice';
import { ApiErrorResponse } from 'src/app/shared/interfaces/api-error-response';

import {
  GetMerchantAdminUsersResponse,
  PostMerchantAdminUserRequest,
  PutMerchantAdminUserRequest,
  User,
  UserResponseModel,
} from '@app/api/models';

export interface MerchantAccountState {
  isCreationLoading: boolean;
  isEditLoading: boolean;
  isTableLoading: boolean;
  merchantAdmins: User[];
  activeUser?: UserResponseModel;
  activeUserLoading: boolean;
  activeUserError: boolean;
  createMerchantAdminErrorResponse?: ApiErrorResponse;
  editMerchantAdminErrorResponse?: ApiErrorResponse;
}

export const initialState: MerchantAccountState = {
  isCreationLoading: false,
  isEditLoading: false,
  isTableLoading: false,
  merchantAdmins: [],
  activeUser: undefined,
  activeUserLoading: false,
  activeUserError: false,
  createMerchantAdminErrorResponse: undefined,
  editMerchantAdminErrorResponse: undefined,
};

export const {
  actions: MerchantAccountActions,
  selectors,
  ...MerchantAccountFeature
} = createSlice({
  name: 'merchant-account',
  initialState,
  reducers: {
    getMerchantAdmins: {
      trigger: (state): MerchantAccountState => {
        return {
          ...state,
          isTableLoading: true,
        };
      },
      success: (
        state,
        action: PayloadAction<GetMerchantAdminUsersResponse>
      ): MerchantAccountState => {
        return {
          ...state,
          isTableLoading: false,
          merchantAdmins: action.merchantAdminUsers ?? [],
        };
      },
    },
    getUser: {
      trigger: (
        state,
        action: PayloadAction<{ userId: string }>
      ): MerchantAccountState => {
        return {
          ...state,
          activeUserLoading: true,
          activeUserError: false,
        };
      },
      success: (
        state,
        action: PayloadAction<{ user: UserResponseModel }>
      ): MerchantAccountState => {
        return {
          ...state,
          activeUser: action.user,
          activeUserLoading: false,
        };
      },
      failure: (state): MerchantAccountState => ({
        ...state,
        activeUserLoading: false,
        activeUserError: true,
      }),
    },
    createMerchantAdmin: {
      trigger: (
        state,
        _action: PayloadAction<PostMerchantAdminUserRequest>
      ): MerchantAccountState => {
        return {
          ...state,
          isCreationLoading: true,
        };
      },
      success: (state, action: PayloadAction<User>): MerchantAccountState => {
        return {
          ...state,
          isCreationLoading: false,
          merchantAdmins: [...state.merchantAdmins, action],
        };
      },
      failure: (
        state,
        action: PayloadAction<ApiErrorResponse>
      ): MerchantAccountState => ({
        ...state,
        isCreationLoading: false,
        createMerchantAdminErrorResponse: action,
      }),
    },
    editMerchantAdmin: {
      trigger: (
        state,
        _action: PayloadAction<{
          userId: string;
          request: PutMerchantAdminUserRequest;
        }>
      ): MerchantAccountState => {
        return {
          ...state,
          isEditLoading: true,
        };
      },
      success: (state): MerchantAccountState => {
        return {
          ...state,
          isEditLoading: false,
        };
      },
      failure: (
        state,
        action: PayloadAction<ApiErrorResponse>
      ): MerchantAccountState => ({
        ...state,
        isEditLoading: false,
        editMerchantAdminErrorResponse: action,
      }),
    },
    removeMerchantAdmin: {
      trigger: noopReducer<{ id: string }>(),
      success: noopReducer(),
    },
    reset: (_) => initialState,
  },
});

export const MerchantAccountSelectors = {
  ...selectors,
};
